import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Eyebrow, FadeUp, MaskedLine } from "@/components/motion";

const EXPECT = [
  ["♠", "Social arrival", "Everyone starts together over a drink"],
  ["♥", "Around three hours", "A relaxed evening, never a marathon"],
  ["♦", "Private Sydney location", "Shared with confirmed Players only"],
  ["♣", "Your pace, always", "Participation is never compulsory"],
];

const FAQS = [
  {
    q: "Do I have to have sex?",
    a: "No. There is never an obligation to play with anyone. Join in, watch, flirt, talk, take your time or simply enjoy being there. Your ticket gets you into the room. Nothing else is compulsory.",
  },
  {
    q: "What if I'm nervous?",
    a: "You're unlikely to be the only one. That's exactly why we start with drinks and conversation rather than dropping everyone straight into the deep end. Give yourself permission to arrive curious rather than confident.",
  },
  {
    q: "What if nobody is interested in me?",
    a: "Attraction is unpredictable — and that's okay. Players Lounge can't promise chemistry between particular people. What we can do is create a friendly, varied room where meeting people is easier, rejection can be handled gracefully and nobody's worth is determined by whether another man wants to play.",
  },
  {
    q: "Is there an age or body type you're looking for?",
    a: "No. Players Lounge is body-positive and welcomes adult men of different ages, shapes, sizes and backgrounds. Curation is about the mix and behaviour of the group, not creating a room full of one particular type of man.",
  },
  {
    q: "Where is it?",
    a: "Players Lounge gatherings are held at private locations in Sydney. To protect everyone's privacy, venue details are only provided to confirmed Players, usually 5–7 days before the event.",
  },
  {
    q: "Can I bring a friend?",
    a: "We like referrals, but every Player needs to join the Players List and be confirmed before attending. Don't simply arrive with an extra man in your hand.",
  },
  {
    q: "How much does it cost?",
    a: "Players Lounge is organised by volunteers and is not run for profit. Ticket prices cover the practical costs of putting on each gathering, including the venue, supplies and cleaning. A ticket is about $35.",
  },
  {
    q: "Will anyone know I've joined?",
    a: "Your information is treated as confidential. We only collect the information we need to communicate with you and help create balanced gatherings. Your personal preferences aren't published or shared with other Players. We expect every guest to show the same discretion.",
  },
];

const FirstGame = () => (
  <div data-testid="first-game-page">
    <section className="lamp-glow mx-auto max-w-7xl px-5 pb-14 pt-36 sm:px-8 sm:pt-44">
      <Eyebrow>First Game?</Eyebrow>
      <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[1.02] tracking-tight text-ivory sm:text-6xl lg:text-7xl">
        <MaskedLine delay={0.1}>Feeling nervous</MaskedLine>
        <MaskedLine delay={0.25} className="italic text-brass">
          is normal.
        </MaskedLine>
      </h1>
      <FadeUp delay={0.4}>
        <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ivory/70">
          That's one reason the evening begins socially. You won't arrive halfway through something
          already happening; everyone starts together, and you have time to meet the room before
          deciding what — if anything — you want to do next.
        </p>
      </FadeUp>
    </section>

    <section className="border-y border-brass/10 bg-charcoal/40">
      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-14 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
        {EXPECT.map(([suit, title, body], i) => (
          <FadeUp key={title} delay={i * 0.06}>
            <div data-testid={`expect-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <span aria-hidden="true" className="text-xl text-brass">
                {suit}
              </span>
              <h2 className="mt-3 font-serif text-xl text-ivory">{title}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-ivory/60">{body}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
      <FadeUp>
        <div className="relative overflow-hidden rounded-2xl border border-brass/15">
          <img
            src="/images/first-game.png"
            alt="Two men in friendly conversation over drinks at a warmly lit lounge bar"
            loading="lazy"
            className="aspect-[16/9] w-full object-cover sm:aspect-[16/8]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-transparent to-transparent" />
          <p className="absolute bottom-6 left-6 font-serif text-xl italic text-ivory sm:bottom-8 sm:left-10 sm:text-2xl">
            Arrive curious. Leave whenever you like.
          </p>
        </div>
      </FadeUp>
    </section>

    <section className="mx-auto max-w-4xl px-5 pb-20 sm:px-8 sm:pb-28">
      <FadeUp>
        <Eyebrow suit="♥">Everything you're wondering</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl text-ivory sm:text-4xl">Asked and answered</h2>
      </FadeUp>
      <Accordion type="single" collapsible defaultValue="faq-0" className="mt-10 space-y-4">
        {FAQS.map((faq, i) => (
          <FadeUp key={faq.q} delay={i * 0.04}>
            <AccordionItem
              value={`faq-${i}`}
              data-testid={`faq-accordion-${i}`}
              className="overflow-hidden rounded-2xl border border-brass/15 bg-charcoal/50 px-6 transition-colors hover:border-brass/35 sm:px-8"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="py-6 text-left hover:no-underline [&>svg]:text-brass"
              >
                <span className="font-serif text-xl text-ivory sm:text-2xl">{faq.q}</span>
              </AccordionTrigger>
              <AccordionContent className="pb-7 pr-2 text-base leading-relaxed text-ivory/70">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          </FadeUp>
        ))}
      </Accordion>

      <FadeUp delay={0.1}>
        <div className="mt-14 text-left">
          <p className="font-serif text-3xl italic text-ivory sm:text-4xl">
            Ready? <span className="text-brass">Deal Me In.</span>
          </p>
          <Link
            to="/deal-me-in"
            data-testid="first-game-deal-me-in-button"
            className="mt-7 inline-flex h-13 items-center rounded-xl bg-burgundy px-9 py-3.5 text-base font-semibold text-ivory transition-all duration-200 hover:bg-[#a33847] hover:shadow-[0_0_30px_rgba(140,46,59,0.5)]"
          >
            Deal Me In
          </Link>
        </div>
      </FadeUp>
    </section>
  </div>
);

export default FirstGame;
